import { Dispatch, SetStateAction } from 'react';

const EPHEMERAL_KEY = 'ephemeral';
export const DIVIDER = '::';

/**
 * Iterates each of the keys in storage and
 * removes any with a match of EPHEMERAL_KEY.
 */
export function clearEphemeralKeys(storage: Storage): void {
  Object.keys(storage).map((key: string) => {
    if (key.includes(EPHEMERAL_KEY)) {
      storage.removeItem(key);
    }
  });
}

/**
 * Returns a function which sets the value in localStorage.
 *
 * @param {string} key
 * @param {Dispatch<SetStateAction<T>>} setter
 * @returns {function}
 */
export function storeValue<T>(key: string, setter: Dispatch<SetStateAction<T>>, storage: Storage) {
  return (value: T | unknown) => {
    if (value === undefined) {
      return;
    }

    if (typeof value === 'number' && isNaN(value)) {
      storage.setItem(key, 'NaN');
    } else {
      storage.setItem(key, JSON.stringify(value));
    }

    setter(value as T);
  };
}

/**
 * Returns a function which removes the given key from storage.
 *
 * @param {string} key
 * @returns {function}
 */
export const clearValue = (key: string, storage: Storage) => () => {
  storage.removeItem(key);
};

/**
 * Gets the value from storage and returning it. If no value exists,
 * or there is an error, then return the initialValue.
 *
 * @param {string} key
 * @param {T} initialValue
 * @returns {function}
 */
export function getValue<T>(key: string, initialValue: T, storage: Storage) {
  return (): T | unknown => {
    try {
      const retrieved = storage.getItem(key);

      if (retrieved === 'NaN') {
        return NaN;
      }

      return retrieved !== undefined && retrieved !== null ? (JSON.parse(retrieved) as T) : initialValue;
    } catch (e) {
      return initialValue;
    }
  };
}

/**
 * Formats a given string with necessary prefixes.
 *
 * @example
 *  formatKey('futrli.useLocalStorage, 'dave', true);
 *  > 'futrli.useLocalStorage::ephemeral::dave'
 *
 *  formatKey('futrli.useLocalStorage, 'other_key', false);
 *  > 'futrli.useLocalStorage::other_key'
 *
 * @param {string} key
 * @param {boolean} ephemeral
 * @returns {string}
 */
export function formatKey(storePrefix: string, key: string, ephemeral?: boolean): string {
  const parts = [storePrefix, key];

  if (ephemeral) {
    parts.splice(1, 0, EPHEMERAL_KEY);
  }

  return parts.join(DIVIDER);
}
